import React, { useState, useEffect } from 'react';
import { PlusIcon, EditIcon, TrashIcon, HelpCircle, CheckCircle } from 'lucide-react';
import api from '../../utils/api';

const EventQuestionsManagement = ({ eventId, eventName, onClose }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question_text: '',
    question_order: 0,
    is_required: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (eventId) {
      fetchQuestions();
    }
  }, [eventId]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/simple.php?action=event-questions&event_id=${eventId}`);
      if (response.data.success) {
        setQuestions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!formData.question_text.trim()) {
      setErrors({ question_text: 'Question text is required' });
      return;
    }

    try {
      if (editingQuestion) {
        await api.post(`/simple.php?action=event-questions&event_id=${eventId}&question_id=${editingQuestion.question_id}&_method=PUT`, formData);
      } else {
        await api.post(`/simple.php?action=event-questions&event_id=${eventId}`, formData);
      }
      
      setFormData({ question_text: '', question_order: 0, is_required: true });
      setEditingQuestion(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      setErrors({ general: 'Failed to save question. Please try again.' });
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      question_order: question.question_order,
      is_required: question.is_required
    });
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await api.post(`/simple.php?action=event-questions&event_id=${eventId}&question_id=${questionId}&_method=DELETE`);
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question');
    }
  };

  const cancelEdit = () => {
    setEditingQuestion(null);
    setFormData({ question_text: '', question_order: 0, is_required: true });
    setErrors({});
  };

  return (
    <div className="overlay">
      <div className="modal" style={{ maxWidth: '800px', width: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>Manage Questions for: {eventName}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="modal-body">
          {/* Add/Edit Question Form */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h3>{editingQuestion ? 'Edit Question' : 'Add New Question'}</h3>
            
            {errors.general && (
              <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                {errors.general}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Question Text *</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={formData.question_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, question_text: e.target.value }))}
                  placeholder="Enter the question you want to ask..."
                  required
                />
                {errors.question_text && <div className="form-error">{errors.question_text}</div>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Order</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.question_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, question_order: parseInt(e.target.value) || 0 }))}
                    min="0"
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Required</label>
                  <select
                    className="form-input"
                    value={formData.is_required}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.value === 'true' }))}
                  >
                    <option value={true}>Yes</option>
                    <option value={false}>No</option>
                  </select>
                </div>
              </div>

              <div className="button-group">
                <button type="submit" className="btn btn-primary">
                  <PlusIcon size={16} />
                  {editingQuestion ? 'Update Question' : 'Add Question'}
                </button>
                {editingQuestion && (
                  <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Questions List */}
          <div className="card">
            <h3>Current Questions ({questions.length})</h3>
            
            {loading ? (
              <div className="loading-spinner">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="empty-state">
                <HelpCircle size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                <p>No questions have been added yet.</p>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                  Add questions that will appear at the end of the evaluation form.
                </p>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Question</th>
                      <th>Required</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions
                      .sort((a, b) => a.question_order - b.question_order || a.question_id - b.question_id)
                      .map((question, index) => (
                      <tr key={question.question_id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ 
                              backgroundColor: '#f3f4f6', 
                              padding: '4px 8px', 
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              #{question.question_order || index + 1}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div style={{ maxWidth: '400px' }}>
                            {question.question_text}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {question.is_required ? (
                              <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: '500' }}>
                                <CheckCircle size={14} style={{ marginRight: '4px' }} />
                                Required
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>Optional</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="button-group" style={{ gap: '8px' }}>
                            <button
                              onClick={() => handleEdit(question)}
                              className="btn btn-icon"
                              title="Edit Question"
                            >
                              <EditIcon size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(question.question_id)}
                              className="btn btn-icon btn-danger"
                              title="Delete Question"
                            >
                              <TrashIcon size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventQuestionsManagement;